import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import * as pdfjsLib from "pdfjs-dist";
import { mediaSrc } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { shareNative, shareWhatsApp, copyLink, downloadFile, saveFiles, canShareFiles } from "../lib/share";
import { toast } from "sonner";
import { X, ZoomIn, ZoomOut, RotateCcw, RotateCw, Share2, MessageCircle, Link2, Download, FileText, PenLine, Forward } from "lucide-react";
import { ForwardDialog } from "./ForwardDialog";
import { MarkupEditor } from "./MarkupEditor";
import { SendMarkupDialog } from "./SendMarkupDialog";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 4;
// Cap per-page canvas bitmap to avoid mobile memory crashes at high zoom
const MAX_CANVAS_PIXELS = 4096 * 4096;

export function PdfViewer({ open, media, onClose }) {
  const { t } = useApp();
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0); // user rotation: 0 | 90 | 180 | 270
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [markupSrc, setMarkupSrc] = useState(null); // snapshot of the visible (zoomed) region
  const [sendData, setSendData] = useState(null);   // {dataUrl, overlays} after markup save
  const containerRef = useRef(null);   // scrollable viewport
  const wrapperRef = useRef(null);     // inner pages wrapper (CSS-transformed during pinch)
  const pdfRef = useRef(null);
  const pageDimsRef = useRef([]);      // cached page sizes at the page's default rotation [{w, h}]
  const renderSeqRef = useRef(0);
  const renderTasksRef = useRef(new Map()); // page number -> in-flight pdf.js render task
  const pinchRef = useRef(null);       // touch pinch {startDist, startZoom, midX, midY, lastRatio}
  const trackpadRef = useRef(null);    // Safari trackpad gesture {startZoom, fx, fy, lastRatio}
  const wheelLiveRef = useRef(null);   // ctrl+wheel live session {target, fx, fy, timer}
  const lastTapRef = useRef({ t: 0, x: 0, y: 0 });
  const zoomRef = useRef(1);
  const rotationRef = useRef(0);
  zoomRef.current = zoom;
  rotationRef.current = rotation;

  const url = media ? mediaSrc(media) : null;
  const filename = media?.filename || "document.pdf";
  const [fileShare, setFileShare] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  useEffect(() => { canShareFiles().then(setFileShare); }, []);

  // ---- Load the PDF document + cache page sizes ----
  useEffect(() => {
    if (!open || !url) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setNumPages(0);
    setZoom(1);
    setRotation(0);
    zoomRef.current = 1;
    rotationRef.current = 0;
    pageDimsRef.current = [];
    const task = pdfjsLib.getDocument({ url });
    task.promise.then(async (pdf) => {
      if (cancelled) return;
      pdfRef.current = pdf;
      const dims = [];
      for (let i = 1; i <= pdf.numPages; i += 1) {
        try {
          const page = await pdf.getPage(i);
          const raw = page.getViewport({ scale: 1 }); // includes the page's own /Rotate
          dims.push({ w: raw.width, h: raw.height });
        } catch (e) {
          dims.push({ w: 612, h: 792 }); // letter fallback
        }
      }
      if (cancelled) return;
      pageDimsRef.current = dims;
      setNumPages(pdf.numPages);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setError(true);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      renderTasksRef.current.forEach((rt) => { try { rt.cancel(); } catch (e) { /* ignore */ } });
      renderTasksRef.current.clear();
      try { task.destroy(); } catch (e) { /* ignore */ }
      pdfRef.current = null;
    };
  }, [open, url]);

  const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  const baseWidth = () => {
    const container = containerRef.current;
    if (!container) return 900;
    return Math.min(container.clientWidth - 24, 900);
  };

  // Effective page size for the current user rotation (swap w/h at 90/270)
  const effDims = (d, rot) => ((rot % 180 !== 0) ? { w: d.h, h: d.w } : d);

  // ---- Synchronously set canvas CSS sizes for all pages at a zoom level ----
  // (instant layout — the crisp repaint follows asynchronously)
  const layoutPages = useCallback((zoomVal) => {
    const container = containerRef.current;
    const dims = pageDimsRef.current;
    if (!container || !dims.length) return;
    const bw = baseWidth();
    const rot = rotationRef.current;
    dims.forEach((d0, idx) => {
      const canvas = container.querySelector(`canvas[data-page="${idx + 1}"]`);
      if (!canvas) return;
      const d = effDims(d0, rot);
      const scale = (bw / d.w) * zoomVal;
      canvas.style.width = `${Math.round(d.w * scale)}px`;
      canvas.style.height = `${Math.round(d.h * scale)}px`;
    });
  }, []);

  // ---- Async crisp repaint of all pages at a zoom level ----
  // Any in-flight render on the same canvas is cancelled first: two pdf.js
  // renders racing on one canvas corrupt the transform stack and produce
  // upside-down / shifted output.
  const paintPages = useCallback(async (zoomVal) => {
    const pdf = pdfRef.current;
    const container = containerRef.current;
    if (!pdf || !container) return;
    const seq = ++renderSeqRef.current;
    const bw = baseWidth();
    const rot = rotationRef.current;
    for (let i = 1; i <= pdf.numPages; i += 1) {
      if (seq !== renderSeqRef.current) return; // superseded by a newer render
      const canvas = container.querySelector(`canvas[data-page="${i}"]`);
      if (!canvas) continue;
      try {
        const page = await pdf.getPage(i);
        const totalRot = ((page.rotate || 0) + rot) % 360;
        const raw = page.getViewport({ scale: 1, rotation: totalRot });
        const scale = (bw / raw.width) * zoomVal;
        const viewport = page.getViewport({ scale, rotation: totalRot });
        let dpr = Math.min(window.devicePixelRatio || 1, 2);
        const px = viewport.width * viewport.height * dpr * dpr;
        if (px > MAX_CANVAS_PIXELS) dpr = Math.sqrt(MAX_CANVAS_PIXELS / (viewport.width * viewport.height));
        if (seq !== renderSeqRef.current) return;
        // Cancel any in-flight render on this canvas BEFORE touching it
        const prevTask = renderTasksRef.current.get(i);
        if (prevTask) { try { prevTask.cancel(); } catch (e) { /* ignore */ } }
        canvas.width = Math.floor(viewport.width * dpr);   // also resets ctx state
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.round(viewport.width)}px`;
        canvas.style.height = `${Math.round(viewport.height)}px`;
        const ctx = canvas.getContext("2d");
        const renderTask = page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
        });
        renderTasksRef.current.set(i, renderTask);
        try {
          await renderTask.promise;
        } catch (e) { /* RenderingCancelledException — a newer render took over */ }
        if (renderTasksRef.current.get(i) === renderTask) renderTasksRef.current.delete(i);
      } catch (e) { /* page render race — ignore */ }
    }
  }, []);

  // ---- Commit a zoom change: instant layout + focal-point scroll anchoring + crisp repaint ----
  // Focal point (fcx, fcy) is in client coordinates; defaults to the viewport center.
  const commitZoom = useCallback((next, fcx, fcy) => {
    const container = containerRef.current;
    if (!container) return;
    const z = clampZoom(next);
    const prev = zoomRef.current;
    if (Math.abs(z - prev) < 0.001) return;
    const rect = container.getBoundingClientRect();
    const fx = (fcx == null ? rect.left + rect.width / 2 : fcx) - rect.left;
    const fy = (fcy == null ? rect.top + rect.height / 2 : fcy) - rect.top;
    const r = z / prev;
    const newLeft = (container.scrollLeft + fx) * r - fx;
    const newTop = (container.scrollTop + fy) * r - fy;
    zoomRef.current = z;
    setZoom(z);
    layoutPages(z);               // sync layout at the new size
    container.scrollLeft = Math.max(0, newLeft);
    container.scrollTop = Math.max(0, newTop);
    paintPages(z);                // crisp async repaint
  }, [layoutPages, paintPages]);

  // ---- Rotate all pages by 90° (fallback for upside-down scanned documents) ----
  const rotatePages = useCallback(() => {
    const next = (rotationRef.current + 90) % 360;
    rotationRef.current = next;
    setRotation(next);
    layoutPages(zoomRef.current);
    paintPages(zoomRef.current);
  }, [layoutPages, paintPages]);

  // Initial layout + paint once the document is ready
  useEffect(() => {
    if (!loading && !error && numPages > 0) {
      layoutPages(zoomRef.current);
      paintPages(zoomRef.current);
    }
  }, [loading, error, numPages, layoutPages, paintPages]);

  // Re-fit on window resize / orientation change (debounced)
  useEffect(() => {
    if (!open) return undefined;
    let timer = null;
    const onResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        layoutPages(zoomRef.current);
        paintPages(zoomRef.current);
      }, 200);
    };
    window.addEventListener("resize", onResize);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, [open, layoutPages, paintPages]);

  // ---- Native (non-passive) gesture listeners ----
  // React >=17 attaches touchmove/wheel as passive, so e.preventDefault() would
  // fail silently and the browser would scroll WHILE pinching (erratic feel).
  // During any gesture we only apply a GPU CSS transform (like PhotoViewer) and
  // commit a single crisp re-render when the gesture ends.
  useEffect(() => {
    const container = containerRef.current;
    if (!open || loading || error || !container) return undefined;

    const dist = (touches) => Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY,
    );

    const beginLive = (fx, fy) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const wrect = wrapper.getBoundingClientRect();
      wrapper.style.transition = "none";
      wrapper.style.willChange = "transform";
      wrapper.style.transformOrigin = `${fx - wrect.left}px ${fy - wrect.top}px`;
    };
    const applyLive = (ratio) => {
      const wrapper = wrapperRef.current;
      if (wrapper) wrapper.style.transform = `scale(${ratio})`;
    };
    const endLive = () => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        wrapper.style.transform = "";
        wrapper.style.transformOrigin = "";
        wrapper.style.willChange = "";
      }
    };

    // ----- Mobile: two-finger touch pinch -----
    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        pinchRef.current = {
          startDist: dist(e.touches),
          startZoom: zoomRef.current,
          midX,
          midY,
          lastRatio: 1,
        };
        beginLive(midX, midY);
      } else if (e.touches.length === 1 && !pinchRef.current) {
        // Double-tap: toggle 1x <-> 2.5x anchored at the tap point
        const now = Date.now();
        const t0 = e.touches[0];
        const lt = lastTapRef.current;
        if (now - lt.t < 300 && Math.hypot(t0.clientX - lt.x, t0.clientY - lt.y) < 40) {
          lastTapRef.current = { t: 0, x: 0, y: 0 };
          commitZoom(zoomRef.current > 1.02 ? 1 : 2.5, t0.clientX, t0.clientY);
        } else {
          lastTapRef.current = { t: now, x: t0.clientX, y: t0.clientY };
        }
      }
    };
    const onTouchMove = (e) => {
      const p = pinchRef.current;
      if (p && e.touches.length === 2) {
        e.preventDefault(); // works — listener registered as non-passive
        const raw = dist(e.touches) / p.startDist;
        const ratio = clampZoom(p.startZoom * raw) / p.startZoom;
        p.lastRatio = ratio;
        applyLive(ratio);
      }
    };
    const onTouchEnd = (e) => {
      const p = pinchRef.current;
      if (p && e.touches.length < 2) {
        pinchRef.current = null;
        endLive();
        commitZoom(p.startZoom * p.lastRatio, p.midX, p.midY);
      }
    };

    // ----- Desktop Chrome/Edge/Firefox: ctrl(or cmd)+wheel = trackpad pinch / mouse zoom -----
    // Live CSS preview + debounced crisp commit (re-rendering on every wheel tick is janky).
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return; // plain wheel scrolls pages naturally
      e.preventDefault();
      if (pinchRef.current || trackpadRef.current) return;
      let s = wheelLiveRef.current;
      if (!s) {
        s = { target: zoomRef.current, fx: e.clientX, fy: e.clientY, timer: null };
        wheelLiveRef.current = s;
        beginLive(e.clientX, e.clientY);
      }
      s.target = clampZoom(s.target * Math.exp(-e.deltaY * 0.0022));
      applyLive(s.target / zoomRef.current);
      if (s.timer) clearTimeout(s.timer);
      s.timer = setTimeout(() => {
        wheelLiveRef.current = null;
        endLive();
        commitZoom(s.target, s.fx, s.fy);
      }, 160);
    };

    // ----- Desktop Safari: proprietary trackpad pinch gesture events -----
    // Without these, Safari zooms the whole browser page instead of the PDF.
    const onGestureStart = (e) => {
      if (pinchRef.current) return; // iOS fires these alongside touch events — touch wins
      e.preventDefault();
      trackpadRef.current = { startZoom: zoomRef.current, fx: e.clientX, fy: e.clientY, lastRatio: 1 };
      beginLive(e.clientX, e.clientY);
    };
    const onGestureChange = (e) => {
      const g = trackpadRef.current;
      if (!g) return;
      e.preventDefault();
      const ratio = clampZoom(g.startZoom * e.scale) / g.startZoom;
      g.lastRatio = ratio;
      applyLive(ratio);
    };
    const onGestureEnd = (e) => {
      const g = trackpadRef.current;
      if (!g) return;
      trackpadRef.current = null;
      e.preventDefault();
      endLive();
      commitZoom(g.startZoom * g.lastRatio, g.fx, g.fy);
    };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchcancel", onTouchEnd, { passive: true });
    container.addEventListener("wheel", onWheel, { passive: false });
    container.addEventListener("gesturestart", onGestureStart);
    container.addEventListener("gesturechange", onGestureChange);
    container.addEventListener("gestureend", onGestureEnd);
    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("gesturestart", onGestureStart);
      container.removeEventListener("gesturechange", onGestureChange);
      container.removeEventListener("gestureend", onGestureEnd);
    };
  }, [open, loading, error, commitZoom]);

  const zoomIn = () => commitZoom(zoomRef.current + 0.35);
  const zoomOut = () => commitZoom(zoomRef.current - 0.35);
  const resetZoom = () => commitZoom(1);

  const doShare = async () => {
    const ok = await shareNative({ url, filename, title: filename });
    if (!ok) toast.info(t("share_unavailable"));
  };

  if (!open || !media) return null;

  // Snapshot exactly what is visible right now (respecting zoom + scroll),
  // so the markup opens directly on the zoomed region.
  const captureVisible = () => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const out = document.createElement("canvas");
    const S = 2; // 2x for crisp markup
    out.width = Math.max(2, Math.floor(rect.width * S));
    out.height = Math.max(2, Math.floor(rect.height * S));
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    let drew = false;
    container.querySelectorAll("canvas[data-page]").forEach((c) => {
      const cr = c.getBoundingClientRect();
      const ix = Math.max(rect.left, cr.left);
      const iy = Math.max(rect.top, cr.top);
      const ix2 = Math.min(rect.right, cr.right);
      const iy2 = Math.min(rect.bottom, cr.bottom);
      if (ix2 - ix < 4 || iy2 - iy < 4 || !c.width || !cr.width) return;
      const rx = c.width / cr.width;
      const ry = c.height / cr.height;
      try {
        ctx.drawImage(
          c,
          (ix - cr.left) * rx, (iy - cr.top) * ry, (ix2 - ix) * rx, (iy2 - iy) * ry,
          (ix - rect.left) * S, (iy - rect.top) * S, (ix2 - ix) * S, (iy2 - iy) * S,
        );
        drew = true;
      } catch (e) { /* canvas not ready */ }
    });
    return drew ? out.toDataURL("image/jpeg", 0.92) : null;
  };

  const openMarkup = () => {
    const snap = captureVisible();
    if (snap) setMarkupSrc(snap);
    else toast.error(t("something_wrong"));
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col pt-safe" style={{ pointerEvents: "auto" }} data-testid="pdf-viewer">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" data-testid="pdf-viewer-close" onClick={onClose} className="text-white hover:bg-white/10 h-11 w-11">
            <X className="h-5 w-5" />
          </Button>
          <FileText className="h-4 w-4 text-white/70 shrink-0" />
          <span className="text-white text-sm truncate max-w-[40vw]" title={filename}>{filename}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" data-testid="pdf-markup-btn" onClick={openMarkup} className="text-white hover:bg-white/10 h-10 w-10">
            <PenLine className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" data-testid="pdf-rotate-btn" onClick={rotatePages} className="text-white hover:bg-white/10 h-10 w-10" aria-label="Rotate">
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" data-testid="pdf-zoom-out" onClick={zoomOut} className="text-white hover:bg-white/10 h-10 w-10">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-white/80 text-xs tabular-nums w-12 text-center" data-testid="pdf-zoom-level">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="icon" data-testid="pdf-zoom-in" onClick={zoomIn} className="text-white hover:bg-white/10 h-10 w-10">
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" data-testid="pdf-zoom-reset" onClick={resetZoom} className="text-white hover:bg-white/10 h-10 w-10">
            <RotateCcw className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="pdf-share-button" className="text-white hover:bg-white/10 h-10">
                <Share2 className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("share")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[210]">
              {(fileShare || (typeof navigator !== "undefined" && !!navigator.share)) && (
                <DropdownMenuItem data-testid="pdf-share-native" onClick={doShare}>
                  <Share2 className="h-4 w-4 mr-2" /> {t("share")}...
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="pdf-share-forward" onClick={() => setForwardOpen(true)}>
                <Forward className="h-4 w-4 mr-2" /> {t("send_to_chat")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="pdf-share-whatsapp" onClick={async () => {
                if (await canShareFiles()) { const r = await saveFiles([{ url, filename }], filename); if (r === "shared" || r === "cancelled") return; }
                shareWhatsApp({ url, text: filename });
              }}>
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="pdf-share-copy"
                onClick={async () => { (await copyLink(url)) ? toast.success(t("link_copied")) : toast.error("Failed"); }}
              >
                <Link2 className="h-4 w-4 mr-2" /> {t("copy_link")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="pdf-share-download"
                onClick={async () => { (await downloadFile({ url, filename })) || toast.error("Failed"); }}
              >
                <Download className="h-4 w-4 mr-2" /> {t("save_file")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Pages */}
      <div
        ref={containerRef}
        data-testid="pdf-pages-container"
        className="flex-1 overflow-auto overscroll-contain px-3 pb-4"
        style={{ touchAction: "pan-x pan-y" }}
      >
        {loading && (
          <div className="text-white/80 text-sm text-center py-16">{t("loading")}...</div>
        )}
        {error && (
          <div className="text-center py-16">
            <p className="text-white/80 text-sm mb-3">{t("pdf_failed")}</p>
            <Button variant="secondary" size="sm" onClick={() => window.open(url, "_blank", "noopener")}>
              <Download className="h-4 w-4 mr-1.5" /> {t("save_file")}
            </Button>
          </div>
        )}
        {!loading && !error && (
          <div ref={wrapperRef} className="flex flex-col items-center gap-3 min-w-fit mx-auto">
            {Array.from({ length: numPages }, (_, i) => (
              <canvas
                key={i + 1}
                data-page={i + 1}
                data-testid={`pdf-page-${i + 1}`}
                className="bg-white rounded shadow-lg max-w-none"
              />
            ))}
          </div>
        )}
      </div>

      {/* Markup over the captured (zoomed) PDF region */}
      <MarkupEditor
        open={!!markupSrc}
        imageSrc={markupSrc}
        onSave={(dataUrl, overlays) => { setSendData({ dataUrl, overlays }); setMarkupSrc(null); }}
        onClose={() => setMarkupSrc(null)}
      />
      <SendMarkupDialog
        open={!!sendData}
        dataUrl={sendData?.dataUrl}
        textOverlays={sendData?.overlays}
        defaultJobId={media?.job_id}
        onClose={() => setSendData(null)}
      />
      <ForwardDialog open={forwardOpen} media={media ? [media] : []} onClose={() => setForwardOpen(false)} />
    </div>,
    document.body
  );
}
